import * as React from 'react';
import ActionButton from '@baidu/icloud-ui-action-button';
import { IconClose } from '@baidu/lsqm-icon';
import { ARRAY_UNIQUE_TAG } from '@formily/core/lib/models';
import { clone } from 'lodash';
import { SortableContainer as sortableContainerHoc, SortableElement as sortableElementHoc, SortableHandle as sortableHandle } from 'react-sortable-hoc';
import c from 'classnames';
import { useValidateBeforeAdd } from '../../hooks';
import { ErrorCollector } from '../../Other/ErrorCollector';
import { Field, useFieldContext } from '../../Other';
import { useArrayUnion } from '../../validator';
import { prefixCls } from '../../utils';
import './ArrayItems.less';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const DragHandle = sortableHandle(() => /*#__PURE__*/_jsx("div", {
  className: `${prefixCls}-row-items-drag-handle`
}));
const SortableItem = sortableElementHoc(props => {
  return /*#__PURE__*/_jsx("div", { ...props,
    children: props.children
  });
});
const SortableList = sortableContainerHoc(props => {
  return /*#__PURE__*/_jsx("div", { ...props,
    children: props.children
  });
});

function useArrayItem(props) {
  const {
    mutators,
    state
  } = useFieldContext();
  useArrayUnion(props.arrayUnion);
  const add = React.useCallback(() => {
    mutators.push(clone(props.itemDefaultValue) || {});
  }, [mutators, props.itemDefaultValue]);
  const remove = mutators.remove;
  const move = mutators.move;
  const value = state.value || []; // effects

  React.useEffect(() => {
    var _state$value;

    if (props.addDefault && !((_state$value = state.value) !== null && _state$value !== void 0 && _state$value.length)) {
      mutators.push(clone(props.itemDefaultValue) || {});
    } // eslint-disable-next-line react-hooks/exhaustive-deps

  }, []);
  return {
    add,
    remove,
    move,
    value
  };
}

export function RowItem(props) {
  const {
    remove,
    index,
    hideRemove,
    children,
    sortable,
    itemsClass,
    Dragger,
    hideItemError
  } = props;
  const del = React.useCallback(() => {
    remove(index);
  }, [index, remove]);
  return /*#__PURE__*/_jsxs("div", {
    className: c(itemsClass, `${prefixCls}-row-items-item`),
    children: [sortable ? Dragger : null, /*#__PURE__*/_jsx(Field, {
      hideUi: true,
      name: index,
      children: children
    }), !hideRemove && /*#__PURE__*/_jsx(IconClose, {
      className: `${prefixCls}-row-items-remove`,
      onClick: del
    }), !hideItemError && /*#__PURE__*/_jsx(ErrorCollector, {
      names: [`${index}.*`]
    })]
  });
}
RowItem.displayName = "RowItem";
export function ArrayItems(props) {
  const {
    addText,
    onAddItem,
    onRemoveItem,
    canRemoveItem,
    hideItemError,
    children,
    min,
    max,
    sortable,
    sortableProps,
    Dragger,
    itemsClass,
    addButtonProps,
    validateBeforeAdd
  } = props;
  const {
    add,
    remove,
    value,
    move
  } = useArrayItem(props);
  const valueRef = React.useRef({
    value
  });
  const handleSort = React.useCallback(({
    oldIndex,
    newIndex
  }) => {
    move(oldIndex, newIndex);
  }, [move]);
  const {
    canAdd
  } = useValidateBeforeAdd(validateBeforeAdd);
  const handleAdd = React.useCallback(async () => {
    if (!(await canAdd())) {
      return;
    }

    add();
    onAddItem && onAddItem();
  }, [add, canAdd, onAddItem]);
  const handleRemove = React.useCallback(async index => {
    if (canRemoveItem) {
      const canRemove = await canRemoveItem(index, valueRef.current.value);

      if (!canRemove) {
        return;
      }
    } // 这里只是简单的使用了index，没有用value的id，对于formItem来说，不一定有id吧


    remove(index);
    onRemoveItem && onRemoveItem(index);
  }, [canRemoveItem, remove, onRemoveItem]);
  const items = React.useMemo(() => {
    const arrLen = value.length;
    return value.map((_, index) => {
      const hideRemove = arrLen <= min && index < min;

      const item = /*#__PURE__*/_jsx(RowItem, {
        itemsClass: itemsClass,
        index: index // eslint-disable-next-line react/jsx-no-bind
        ,
        remove: () => handleRemove(index),
        hideRemove: hideRemove,
        sortable: sortable,
        Dragger: Dragger,
        hideItemError: hideItemError,
        children: children
      }, _[ARRAY_UNIQUE_TAG]);

      if (sortable) {
        return /*#__PURE__*/_jsx(SortableItem, {
          index: index,
          children: item
        }, _[ARRAY_UNIQUE_TAG]);
      }

      return item;
    });
  }, [value, min, itemsClass, sortable, Dragger, hideItemError, children, handleRemove]);
  const list = React.useMemo(() => {
    return sortable ? /*#__PURE__*/_jsx(SortableList, {
      useDragHandle: true,
      onSortEnd: handleSort,
      ...sortableProps,
      children: items
    }) : items;
  }, [handleSort, items, sortable, sortableProps]);
  const arrLen = value.length;
  const showAdd = max == null ? true : arrLen < max;
  return /*#__PURE__*/_jsxs("div", {
    children: [list, showAdd && /*#__PURE__*/_jsx(ActionButton, {
      className: `${prefixCls}-row-items-add`,
      action: "create",
      ...addButtonProps,
      onClick: handleAdd,
      children: addText
    })]
  });
}
ArrayItems.displayName = "ArrayItems";
ArrayItems.defaultProps = {
  addDefault: true,
  addText: '添加',
  min: 1,
  sortable: false,
  Dragger: /*#__PURE__*/_jsx(DragHandle, {})
};
//# sourceMappingURL=es/components/ArrayItems/ArrayItems.js.map